import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    honeypot: ''
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Honeypot spam protection
    if (formData.honeypot) {
      return;
    }

    // Validate fields
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({
        type: 'error',
        message: 'Please fill in all required fields.'
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      // Send email using EmailJS
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID',
        {
          from_name: formData.name.trim(),
          from_email: formData.email.trim(),
          message: formData.message.trim(),
          to_email: 'Info@testacare.co.uk, arshadashraf@testacare.co.uk'
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY'
      );

      setStatus({
        type: 'success',
        message: 'Thank you for contacting us. We will get back to you soon.'
      });

      setFormData({
        name: '',
        email: '',
        message: '',
        honeypot: ''
      });

      setTimeout(() => {
        setStatus({ type: null, message: '' });
      }, 5000);

    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later or email us directly at Info@testacare.co.uk'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-us flex relative min-h-screen">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/assets/images/join our team.jpeg')" }}
      >
        <div className="absolute inset-0 bg-[#3f77b6] bg-opacity-85"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full flex flex-col lg:flex-row max-w-7xl mx-auto px-8 lg:px-16 py-16 gap-12">
        {/* Left Side - Copy Section */}
        <div className="contact-us__copy w-full lg:w-1/2 text-white">
          <div
            id="referrals"
            className="mb-4 text-sm uppercase tracking-[0.3em] text-white/90"
          >
            Referrals &amp; Contact
          </div>
          <h3 className="text-4xl lg:text-5xl font-light mb-8">
            Get in touch
          </h3>
          <p className="text-lg lg:text-xl mb-6 leading-relaxed">
            If you would like to contact Testa Care, please complete this form. We would be very happy to hear from&nbsp;you.
          </p>
          <p className="text-lg lg:text-xl mb-6 leading-relaxed text-white/90">
            <strong>Referrals and placements:</strong>{' '}
            <a
              href="mailto:referrals@testacare.co.uk"
              className="text-white underline decoration-white/60 hover:text-white/80 transition"
            >
              referrals@testacare.co.uk
            </a>
          </p>
          <p className="text-lg lg:text-xl mb-6 leading-relaxed text-white/90">
            <strong>Email:</strong>{' '}
            <a
              href="mailto:Info@testacare.co.uk"
              className="text-white underline decoration-white/60 hover:text-white/80 transition"
            >
              Info@testacare.co.uk
            </a>
          </p>
          <p className="text-lg lg:text-xl mb-6 leading-relaxed text-white/90">
            <strong>Address:</strong><br />
            Davyfield Road,<br />
            Blackburn,<br />
            Lancashire,<br />
            England,<br />
            BB1 2QY.
          </p>
          <p className="text-lg lg:text-xl mb-6 leading-relaxed text-white/90">
            If you would like to apply to be part of the Testa Care family then visit our{' '}
            <a href="/join-our-team" className="text-white underline decoration-white/60 hover:text-white/80 transition">
              careers&nbsp;page
            </a>
          </p>
        </div>

        {/* Right Side - Form Section */}
        <div className="contact-us__form w-full lg:w-1/2">
          <form className="form space-y-6" onSubmit={handleSubmit}>
            {/* Honeypot field for spam protection */}
            <input
              name="honeypot"
              style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={formData.honeypot}
              onChange={handleChange}
            />

            {/* Name Field */}
            <div className="form-group mb-6">
              <label className="block text-lg text-white mb-2" htmlFor="name">
                Your name
              </label>
              <input
                className="w-full px-4 py-3 bg-white border-0 focus:outline-none focus:ring-2 focus:ring-orange-500"
                id="name"
                type="text"
                name="name"
                placeholder=""
                required
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            {/* Email Field */}
            <div className="form-group mb-6">
              <label className="block text-lg text-white mb-2" htmlFor="email">
                Your email address
              </label>
              <input
                className="w-full px-4 py-3 bg-white border-0 focus:outline-none focus:ring-2 focus:ring-orange-500"
                id="email"
                type="email"
                name="email"
                placeholder=""
                required
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {/* Message Field */}
            <div className="form-group mb-6">
              <label className="block text-lg text-white mb-2" htmlFor="message">
                Your message
              </label>
              <textarea
                className="w-full px-4 py-3 bg-white border-0 focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows="6"
                id="message"
                name="message"
                placeholder=""
                required
                value={formData.message}
                onChange={handleChange}
              ></textarea>
            </div>

            {status.type && (
              <div
                className={`text-sm px-4 py-3 rounded ${
                  status.type === 'success' ? 'bg-green-600/20 text-green-200' : 'bg-red-600/20 text-red-200'
                }`}
              >
                {status.message}
              </div>
            )}

            {/* Submit Button */}
            <div className="form__btn flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-full border-2 border-orange-500 text-white hover:bg-orange-500 px-8 py-3 text-lg font-medium transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
